import React, { useCallback, useRef, useState } from 'react';
import { ScreenProps } from '../../../typesAndInterfaces/componentProps';
import Begin from './views/Begin';
import { useNavigation } from '@react-navigation/core';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Answer, RootStackParamList } from '../../../navigations/RootNavigator';
import GuessStep from './views/GuessStep';
import BetStep from './views/BetStep';
import AnswerStep from './views/AnswerStep';
import OtherGroupBonusStep from './views/OtherGroupBonusStep';
import IdealBonusStep from './views/IdealBonusStep';
import { getNumberOfSteps } from '../../../utils/shared';
import { trpc } from '../../../apiCalls/trpcClient';
import { utils } from '../../../utils/utils';
import { STRING_CONSTANTS } from '../../../constants/constants';
import Complete from './views/Complete';
import { AddQuarterAnswerResponse } from '@sapphicsavvy/business';
import { usePrefetchImages } from '../../../hooks/usePrefetchImages';
import { mbGetMediumImage } from '@mightybyte/rnw.components.image';

export default function Quarter({ route }: ScreenProps<'Quarter'>) {
    const { quarterData, questionIndex, step, answer } = route.params;

    const [isBeginVisible, setIsBeginVisible] = useState(
        questionIndex === 0 && step === 'Guess',
    );
    const [isCompleteVisible, setIsCompleteVisible] = useState(false);

    const navigation =
        useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const question = quarterData.questions[questionIndex];
    const trpcUtils = trpc.useUtils();
    const pointsRef = useRef<AddQuarterAnswerResponse>();
    const { mutate: addQuarterAnswer, isLoading: isAddingAnswer } =
        trpc.quarter.addQuarterAnswer.useMutation();

    usePrefetchImages({
        urls: quarterData.questions.map((q) => mbGetMediumImage(q.imageUrl)),
        enabled: isBeginVisible,
    });

    if (!question) {
        throw new Error('no question found.');
    }

    const numberOfSteps = getNumberOfSteps(question);
    //at the "begin" screen on client's request we should show begin quarterPoints + points user will earn for answering first question
    // this is just UI, to avoid confusion that may happen when next scree will show extra +50 points.
    //backend will handle initial points, frontend shouldn't add 50 for first question
    const points = quarterData.currentQuarterPointsTotal + 50 * questionIndex;

    const onNext = useCallback(
        (partialAnswer: Partial<Answer>) => {
            const newAnswer = { ...answer, ...partialAnswer };
            if (isBeginVisible) {
                setIsBeginVisible(false);
            } else if (isCompleteVisible) {
                navigation.reset({
                    routes: [{ name: 'Tabs', params: { name: 'Home' } }],
                    index: 1,
                });
            } else if (step === 'Guess') {
                navigation.push('Quarter', {
                    ...route.params,
                    step: 'Bet',
                    answer: newAnswer,
                });
            } else if (step === 'Bet') {
                navigation.push('Quarter', {
                    ...route.params,
                    step: 'Answer',
                    answer: newAnswer,
                });
            } else if (step === 'Answer' && numberOfSteps > 3) {
                navigation.push('Quarter', {
                    ...route.params,
                    step: question.identityGroupBonus
                        ? 'OtherGroupBonus'
                        : 'IdealBonusStep',
                    answer: newAnswer,
                });
            } else if (step === 'OtherGroupBonus' && numberOfSteps > 4) {
                navigation.push('Quarter', {
                    ...route.params,
                    step: 'IdealBonusStep',
                    answer: newAnswer,
                });
            } else {
                const nextQuestionIndex = questionIndex + 1;
                const answers = [...route.params.answers, newAnswer];
                if (nextQuestionIndex < quarterData.questions.length) {
                    navigation.push('Quarter', {
                        ...route.params,
                        questionIndex: nextQuestionIndex,
                        step: 'Guess',
                        answer: {
                            questionId:
                                quarterData.questions[nextQuestionIndex].id,
                            answerType:
                                quarterData.questions[nextQuestionIndex]
                                    .answerType,
                        },
                        answers,
                    });
                } else {
                    addQuarterAnswer(
                        {
                            quarterId: quarterData.id,
                            data: answers.map((e) => ({
                                questionId: e.questionId,
                                answerType: e.answerType,
                                guess: e.guess || 0,
                                extraBet: e.extraBet || 0,
                                answer: e.answer || 0,
                                identityGroupBonus: e.identityGroupBonus,
                                ageGroupBonus: e.ageGroupBonus,
                                idealBonus: e.idealBonus,
                            })),
                        },
                        {
                            onSuccess: (data) => {
                                trpcUtils.quarter.haveToPlay.invalidate();
                                pointsRef.current = { ...data };
                                setIsCompleteVisible(true);
                            },
                            onError: (error) =>
                                utils.showPopUp(
                                    error.message ??
                                        STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN,
                                ),
                        },
                    );
                }
            }
        },
        [
            addQuarterAnswer,
            answer,
            isBeginVisible,
            isCompleteVisible,
            navigation,
            numberOfSteps,
            quarterData.id,
            quarterData.questions,
            question.identityGroupBonus,
            questionIndex,
            route.params,
            step,
            trpcUtils.quarter.haveToPlay,
        ],
    );

    const onBack = useCallback(() => {
        navigation.goBack();
    }, [navigation]);

    if (isBeginVisible) {
        return (
            <Begin
                points={quarterData.currentQuarterPointsTotal}
                onPress={() => onNext({})}
                quarterNumber={quarterData.quarterNumber}
            />
        );
    } else if (isCompleteVisible) {
        if (!pointsRef.current) {
            throw new Error('[Quarter] points is missing');
        }
        return (
            <Complete
                quarterData={quarterData}
                points={pointsRef.current}
                onPress={() => onNext({})}
            />
        );
    } else if (step === 'Guess') {
        return (
            <GuessStep
                question={question}
                points={points}
                onNext={onNext}
                onBack={onBack}
            />
        );
    } else if (step === 'Bet') {
        return (
            <BetStep
                question={question}
                answer={answer}
                points={points}
                onNext={onNext}
                onBack={onBack}
            />
        );
    } else if (step === 'Answer') {
        return (
            <AnswerStep
                question={question}
                points={points}
                onNext={onNext}
                onBack={onBack}
                isSubmitting={isAddingAnswer}
            />
        );
    } else if (step === 'OtherGroupBonus') {
        return (
            <OtherGroupBonusStep
                question={question}
                points={points}
                onNext={onNext}
                onBack={onBack}
                isSubmitting={isAddingAnswer}
            />
        );
    } else if (step === 'IdealBonusStep') {
        return (
            <IdealBonusStep
                question={question}
                points={points}
                onNext={onNext}
                onBack={onBack}
                isSubmitting={isAddingAnswer}
            />
        );
    }

    return null;
}
